import { events, pageViewTypes } from "@constants/tracking";
/* ----------------- React --------------- */
import { useMemo, useState } from "react";

import { useCustomNavigation } from "@hooks/useCustomNavigation";
import { FeedbackDialogContainer } from "../../../containers/FeedbackDialogContainer";
/* ----------------- Hooks --------------- */
import { useMoreSettings } from "../hooks/useMoreSettings";

import { PurchaseDialog } from "@features/dashboard/components/PurchaseDialog";
import { ExamsFilterDialog } from "components/ExamsFilterDialog";
import { Portal } from "react-native-paper";

import { ChangeAvatarDialog } from "@features/profile/dialogs/ChangeAvatarDialog";
import { isWeb } from "@helpers/platform";
import useAnalytics from "@hooks/useAnalytics";
import { useAppStore } from "@hooks/useAppStore";
import { useGlobalStore } from "@hooks/useGlobalStore";
import type { ProductType } from "@memorang/types";
import MoreApplet from "@memorang/ui/components/MoreApplet";
import type { FontSchemaType } from "@memorang/ui/font";
import { trackCustomEvent } from "analytics";
import SnackBarCustom from "components/CustomSnackbar";
import SetExamDateContainer from "components/SetExamDateContainer";
/* ----------------- Helpers --------------- */
import { router, useNavigation } from "expo-router";
/* ----------------- Types --------------- */
import type { MoreSettingsItem } from "../types";

const MoreRouteContainer = () => {
	const [showFeedbackDialog, setShowFeedbackDialog] = useState(false);
	const {
		moreSettingsSections,
		showDatePicker,
		toggleShowDatePicker,
		showExamsFilterDialog,
		toggleShowExamsFilterDialog,
		purchasedProducts,
		hasMoreThanOneExam,
	} = useMoreSettings();
	const memoizedAnalyticsParams = useMemo(() => {
		return {
			type: pageViewTypes.more,
		};
	}, []);
	useAnalytics(memoizedAnalyticsParams);
	const setShowAvatarUpdatedMessage = useGlobalStore(
		(state) => state.setShowAvatarUpdatedMessage,
	);

	const showAvatarUpdatedMessage = useGlobalStore(
		(state) => state.showAvatarUpdatedMessage,
	);
	const toggleShowFeedbackDialog = () => {
		setShowFeedbackDialog((prev) => !prev);
	};
	const [selectedProductType, setSelectedProductType] = useState<
		ProductType | undefined
	>(undefined);
	const [showChangeAvatarDialog, setShowChangeAvatarDialog] = useState(false);
	const { handleChangeAvatarNavigation } = useCustomNavigation();

	const toggleChangeAvatarDialog = () => {
		setShowChangeAvatarDialog(!showChangeAvatarDialog);
	};

	const { handleExamsFilterNavigation } = useCustomNavigation();

	const handleNavigateToPurchaseView = (id?: string) => {
		const productItem = purchasedProducts?.find(
			(item) => item.bundle.id === id,
		);
		if (productItem) {
			if (isWeb) {
				setSelectedProductType(productItem.bundle.bundleType);
			} else {
				router.push({
					pathname: "/purchase",
					params: {
						productType: productItem.bundle.bundleType,
					},
				});
			}
		}
	};
	const appFont = useAppStore((store) => store.app.font);

	const handleClickMoreSettingsItem = (item: MoreSettingsItem) => {
		switch (item.type) {
			case "profile":
				router.push("/profile");
				break;
			case "account-settings":
				router.push({
					pathname: "/account/settings",
					params: { showBackButton: "true" },
				});
				break;
			case "change-date":
				toggleShowDatePicker();
				break;
			case "switch-exam":
				if (hasMoreThanOneExam) {
					handleExamsFilterNavigation(toggleShowExamsFilterDialog);
				}
				break;
			case "subscription":
				handleNavigateToPurchaseView(item.id);
				break;
			case "change-avatar":
				handleChangeAvatarNavigation(toggleChangeAvatarDialog);
				break;
			case "redeem":
				router.push("/redeem");
				break;
			default:
				break;
		}
	};

	const showPurchaseDialog = !!selectedProductType;
	const navigation = useNavigation<{ toggleDrawer?: () => void }>();
	return (
		<>
			<MoreApplet
				sections={moreSettingsSections}
				handleClickMoreSettingsItem={handleClickMoreSettingsItem}
				appFont={appFont as FontSchemaType}
				customAppBar={{
					title: "More Settings",
					navigation,
					handleTrackEvent: () => {
						trackCustomEvent({
							eventName: events.commandBarClicked,
							option: "command_bar",
						});
					},
				}}
			/>
			<SetExamDateContainer
				showDatePicker={showDatePicker}
				toggleShowDatePicker={toggleShowDatePicker}
			/>
			{showExamsFilterDialog && (
				<ExamsFilterDialog
					visible={showExamsFilterDialog}
					handleClose={toggleShowExamsFilterDialog}
				/>
			)}
			{showPurchaseDialog ? (
				<PurchaseDialog
					visible={showPurchaseDialog}
					handleClose={() => setSelectedProductType(undefined)}
					productType={selectedProductType}
				/>
			) : null}
			{showChangeAvatarDialog && (
				<ChangeAvatarDialog
					visible={showChangeAvatarDialog}
					handleClose={toggleChangeAvatarDialog}
				/>
			)}
			{showAvatarUpdatedMessage && (
				<Portal>
					<SnackBarCustom
						message="Avatar updated successfully"
						duration={2000}
						visible
						handleDismiss={() => {
							setShowAvatarUpdatedMessage(false);
						}}
					/>
				</Portal>
			)}
			{isWeb && (
				<FeedbackDialogContainer
					visible={showFeedbackDialog}
					onDismiss={toggleShowFeedbackDialog}
				/>
			)}
		</>
	);
};

export default MoreRouteContainer;
